import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { FileText, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <CardTitle className="text-2xl">End User License Agreement</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  WorkSafeAgent LLC - Effective Date: December 14, 2025
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <div className="space-y-6">
              <section>
                <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
                <p>
                  BY ACCESSING OR USING THE OSHA SAFETY AUDIT APPLICATION AND RELATED SERVICES (THE "SERVICES") PROVIDED BY 
                  WORKSAFEAGENT LLC ("COMPANY," "WE," "US," OR "OUR"), YOU ("USER," "YOU," OR "YOUR") AGREE TO BE BOUND BY 
                  THIS END USER LICENSE AGREEMENT ("AGREEMENT"). IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE SERVICES.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">2. License Grant</h2>
                <p>
                  Subject to your compliance with this Agreement, we grant you a limited, non-exclusive, non-transferable, 
                  revocable license to access and use the Services for your internal business purposes only. This license 
                  does not include any resale or commercial use of the Services, or any derivative use of the Services.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">3. User Responsibilities</h2>
                <p>You agree to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide accurate, current, and complete information during registration</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized access to your account</li>
                  <li>Use the Services in compliance with all applicable laws and regulations</li>
                  <li>Not attempt to interfere with, compromise, or disrupt the Services</li>
                  <li>Not use automated systems to access the Services without our written permission</li>
                  <li>Not reverse engineer, decompile, or attempt to extract source code from the Services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">4. DISCLAIMER OF WARRANTIES</h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="font-semibold uppercase">
                    THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
                  </p>
                  <p className="mt-2">
                    TO THE FULLEST EXTENT PERMITTED BY LAW, WORKSAFEAGENT LLC DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, 
                    INCLUDING BUT NOT LIMITED TO:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>IMPLIED WARRANTIES OF MERCHANTABILITY</li>
                    <li>FITNESS FOR A PARTICULAR PURPOSE</li>
                    <li>NON-INFRINGEMENT</li>
                    <li>ACCURACY, RELIABILITY, OR COMPLETENESS OF CONTENT</li>
                    <li>THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE</li>
                    <li>THAT DEFECTS WILL BE CORRECTED</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">5. SAFETY DISCLAIMER</h2>
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <p className="font-semibold uppercase text-red-700">CRITICAL SAFETY NOTICE:</p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>
                      <strong>The Services are tools only.</strong> They DO NOT replace professional safety judgment, 
                      qualified safety personnel, or compliance with OSHA regulations.
                    </li>
                    <li>
                      <strong>AI-generated content</strong> (including safety recommendations, translations, and analysis) 
                      may contain errors, inaccuracies, or omissions. Always verify AI-generated content with qualified 
                      safety professionals.
                    </li>
                    <li>
                      <strong>You are solely responsible</strong> for workplace safety decisions, OSHA compliance, and 
                      implementation of safety measures.
                    </li>
                    <li>
                      <strong>We do not guarantee</strong> that use of the Services will prevent accidents, injuries, 
                      or OSHA violations.
                    </li>
                    <li>
                      <strong>Translations</strong> provided by the Services may not be accurate for technical safety 
                      terminology. Always have critical safety communications verified by qualified translators.
                    </li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">6. LIMITATION OF LIABILITY</h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="font-semibold uppercase">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, WORKSAFEAGENT LLC AND ITS OFFICERS, DIRECTORS, EMPLOYEES, 
                    AGENTS, AFFILIATES, AND LICENSORS SHALL NOT BE LIABLE FOR:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                    <li>LOSS OF PROFITS, REVENUE, DATA, OR USE</li>
                    <li>WORKPLACE ACCIDENTS, INJURIES, OR FATALITIES</li>
                    <li>OSHA FINES, PENALTIES, OR VIOLATIONS</li>
                    <li>BUSINESS INTERRUPTION OR LOSS OF OPPORTUNITY</li>
                    <li>ERRORS OR OMISSIONS IN AI-GENERATED CONTENT</li>
                    <li>TRANSLATION ERRORS OR MISCOMMUNICATIONS</li>
                    <li>RELIANCE ON INFORMATION PROVIDED BY THE SERVICES</li>
                  </ul>
                  <p className="mt-3 font-semibold">
                    IN NO EVENT SHALL WORKSAFEAGENT LLC'S TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID TO US IN THE 
                    12 MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS LESS.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">7. INDEMNIFICATION</h2>
                <p>
                  You agree to indemnify, defend, and hold harmless WorkSafeAgent LLC and its officers, directors, employees, 
                  agents, affiliates, and licensors from and against any claims, liabilities, damages, losses, costs, expenses, 
                  or fees (including reasonable attorneys' fees) arising from:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Your use or misuse of the Services</li>
                  <li>Your violation of this Agreement</li>
                  <li>Your violation of any laws or regulations</li>
                  <li>Workplace accidents, injuries, or safety violations</li>
                  <li>Your reliance on information provided by the Services</li>
                  <li>Any third-party claims related to your use of the Services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">8. Subscription and Payment</h2>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Subscription fees are charged in advance on a recurring basis</li>
                  <li>You authorize us to charge your payment method for all fees</li>
                  <li>Fees are non-refundable except as required by law</li>
                  <li>We may change fees upon 30 days' notice</li>
                  <li>Failure to pay may result in suspension or termination of Services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">9. Intellectual Property</h2>
                <p>
                  All intellectual property rights in the Services, including software, content, trademarks, and logos, 
                  are owned by WorkSafeAgent LLC or its licensors. You do not acquire any ownership rights by using the Services.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">10. Data and Content</h2>
                <ul className="list-disc pl-6 space-y-1">
                  <li>You retain ownership of your data and content submitted to the Services</li>
                  <li>You grant us a license to use your data to provide and improve the Services</li>
                  <li>We may use anonymized and aggregated data for analytics and research</li>
                  <li>You are responsible for backing up your data</li>
                  <li>We may delete your data upon termination of your account</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">11. Termination</h2>
                <p>
                  We may suspend or terminate your access to the Services at any time, with or without cause, with or 
                  without notice. Upon termination, your license to use the Services immediately ceases. Sections 4, 5, 6, 7, 
                  9, 12, 13, and 14 shall survive termination.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">12. Governing Law and Dispute Resolution</h2>
                <p>
                  This Agreement shall be governed by the laws of the United States and the state where WorkSafeAgent LLC 
                  is registered, without regard to conflict of law principles. Any disputes shall be resolved through 
                  binding arbitration in accordance with the American Arbitration Association rules.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">13. Changes to Terms</h2>
                <p>
                  We reserve the right to modify this Agreement at any time. We will provide notice of material changes 
                  by posting the updated Agreement on our Services. Your continued use after changes constitutes acceptance 
                  of the modified Agreement.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">14. Contact Information</h2>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">WorkSafeAgent LLC</p>
                  <p>Email: legal@wsafeai.com</p>
                  <p>Website: www.wsafeai.com</p>
                </div>
              </section>

              <section className="border-t pt-6 mt-8">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="font-semibold">ACKNOWLEDGMENT</p>
                  <p className="mt-2">
                    BY CLICKING "I AGREE," REGISTERING FOR AN ACCOUNT, OR USING THE SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE 
                    READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THIS AGREEMENT, INCLUDING THE DISCLAIMERS AND LIMITATIONS OF 
                    LIABILITY.
                  </p>
                </div>
                <p className="text-sm text-gray-600 italic mt-4">
                  © 2025-2026 WorkSafeAgent LLC. All rights reserved.
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
